import { useContext, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { CourseDetailsContext } from '..';
import useGlobalStore from 'store/global';
import styles from './ReviewSection.module.scss';
import { CourseReviewItem, getReviews } from 'service/course';
import InfiniteScroll from 'components/common/InfiniteScroll';
import MemoizedReviewItem from './ReviewItem';
import ReviewCommentBox from './ReviewCommentBox';
import Icon from 'components/common/Icon';
import { Rating } from '@material-ui/lab';
import classNames from 'classnames';
import { ratingNumberWithDecimal } from 'common/utils/course';

const ReviewSection = () => {
  const { courseInfo } = useContext(CourseDetailsContext);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const [currentReview, setCurrentReview] = useState<CourseReviewItem>();
  const { data, fetchNextPage, isFetchingNextPage, refetch } = useInfiniteQuery(
    ['getReviews', courseInfo.id?.toString()],
    async ({ pageParam = 0 }) => {
      return await getReviews({
        courseId: courseInfo.id,
        lastId: pageParam as number,
      });
    },
    {
      getNextPageParam: (lastPage) => {
        const reviews = lastPage?.data?.reviews || [];
        if (reviews && reviews.length > 0) {
          const lastRv = reviews[reviews.length - 1];
          return lastRv.id;
        }
      },
      onSuccess: (data) => {
        const newReview = data.pages[0].data?.review;
        if (
          newReview &&
          (!currentReview || currentReview?.id !== newReview.id)
        ) {
          setCurrentReview(newReview);
        }
      },
      enabled: isAuthInit,
      refetchOnWindowFocus: false,
    }
  );

  const pages = data?.pages;
  const reviewItems: CourseReviewItem[] = useMemo(
    () =>
      pages
        ? pages.reduce<CourseReviewItem[]>((curr, page) => {
            const rvs = page.data?.reviews || [];
            return curr.concat(rvs);
          }, [])
        : [],
    [pages]
  );

  const ratingsCount = useMemo(() => {
    return courseInfo.review_summary?.ratings?.reduce((curr, r) => {
      return curr + r.count;
    }, 0);
  }, [courseInfo.review_summary?.ratings]);

  return (
    <div className={styles.reviewSection}>
      <div className={styles.courseRatingSection}>
        <div className={styles.ratingSummary}>
          <div className={styles.ratingNumber}>
            {ratingNumberWithDecimal(courseInfo.review_summary?.rating)}
          </div>
          <Rating
            readOnly
            value={courseInfo.review_summary?.rating || 0}
            size="large"
            name="course-rating"
          />
          <div className={styles.reviewsCount}>
            {courseInfo.review_summary?.count}件
          </div>
        </div>
        <div className={styles.ratingDetailsSection}>
          {[5, 4, 3, 2, 1].map((rating) => {
            const count =
              courseInfo.review_summary?.ratings?.find(
                (r) => r.rating === rating
              )?.count || 0;
            const offWithPercent =
              ratingsCount === 0
                ? 100
                : Math.floor(100 - (count / ratingsCount) * 100);
            return (
              <div className={styles.ratingDetailItem} key={`rating-${rating}`}>
                <span className={styles.rating}>{rating}</span>
                <div className={styles.progressBar}>
                  <div
                    className={classNames(styles.offOverlay, {
                      [styles.fullWidth]: offWithPercent === 100,
                    })}
                    style={{
                      width: `${offWithPercent}%`,
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {!courseInfo.is_owner &&
        courseInfo.is_purchased &&
        !courseInfo.review && (
          <div className={styles.addReviewBox}>
            <ReviewCommentBox
              callback={() => {
                setTimeout(() => {
                  void refetch();
                }, 500);
              }}
            />
          </div>
        )}
      <div className={styles.reviewsListWrapper}>
        <div className={styles.reviewsListTitle}>全てのレビュー</div>
        {reviewItems.length === 0 && !currentReview && (
          <div className={styles.empty}>
            <Icon name="empty" width={96} height={96} />
            <div>レビューはありません</div>
          </div>
        )}
        {currentReview && <MemoizedReviewItem reviewItem={currentReview} />}
        <InfiniteScroll
          fetchNextPage={fetchNextPage}
          isLoading={isFetchingNextPage}
        >
          {reviewItems.map((rv, index) => {
            return (
              <MemoizedReviewItem reviewItem={rv} key={`review-${index}`} />
            );
          })}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ReviewSection;
